import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { LogOut, ArrowLeft, Plus, Pencil, Trash2, Loader2, Star } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIMES = [
  { value: "contract", label: "Contrato (Outsourcing)" },
  { value: "perm", label: "Perm" },
  { value: "green_receipts", label: "Recibos Verdes" },
  { value: "b2b", label: "B2B" },
];

const defaultForm = {
  client_name: "",
  fee_percentage: 0,
  fee_fixed: 0,
  applies_to: ["perm"],
  description: "",
  active: true,
};

const AdminClientSpecialFees = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(defaultForm);

  useEffect(() => { loadItems(); }, []);

  const loadItems = async () => {
    try {
      const r = await axios.get(`${API}/client-special-fees`, { headers: getAuthHeaders() });
      setItems(r.data || []);
    } catch (e) {
      toast.error("Erro ao carregar");
    } finally { setLoading(false); }
  };

  const openNew = () => { setEditing(null); setFormData(defaultForm); setIsDialogOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setFormData({
      client_name: it.client_name,
      fee_percentage: it.fee_percentage || 0,
      fee_fixed: it.fee_fixed || 0,
      applies_to: it.applies_to || ["perm"],
      description: it.description || "",
      active: it.active,
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        fee_percentage: parseFloat(formData.fee_percentage) || 0,
        fee_fixed: parseFloat(formData.fee_fixed) || 0,
      };
      if (editing) {
        await axios.put(`${API}/client-special-fees/${editing.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Atualizado!");
      } else {
        await axios.post(`${API}/client-special-fees`, payload, { headers: getAuthHeaders() });
        toast.success("Criado!");
      }
      setIsDialogOpen(false);
      loadItems();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar?")) return;
    await axios.delete(`${API}/client-special-fees/${id}`, { headers: getAuthHeaders() });
    toast.success("Eliminado");
    loadItems();
  };

  const toggleRegime = (regime) => {
    const cur = formData.applies_to || [];
    setFormData({ ...formData, applies_to: cur.includes(regime) ? cur.filter(r => r !== regime) : [...cur, regime] });
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-client-special-fees-page">
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-lg px-3 py-1.5">
              <img src="/evolutt_logo_white.png" alt="Evolutt" className="h-8" />
            </div>
            <p className="text-xs text-muted-foreground">Situações Especiais (Sub-fees por Cliente)</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/"); }}>
              <LogOut className="w-4 h-4 mr-2" /> Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin"><Button variant="outline" size="sm"><ArrowLeft className="w-4 h-4 mr-2" />Voltar</Button></Link>
            <div>
              <h1 className="font-manrope font-extrabold text-2xl">Situações Especiais</h1>
              <p className="text-muted-foreground">Sub-fees ou % específicas por cliente</p>
            </div>
          </div>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand-600 text-white hover:bg-brand-700 font-bold" onClick={openNew} data-testid="add-client-special-btn">
                <Plus className="w-4 h-4 mr-2" />Novo Cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-manrope font-bold text-xl">
                  {editing ? "Editar" : "Novo Cliente Especial"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Nome do Cliente</Label>
                  <Input value={formData.client_name} onChange={(e) => setFormData({ ...formData, client_name: e.target.value })} placeholder="Ex.: Acme Corp" required data-testid="client-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Fee (%)</Label>
                    <Input type="number" step="0.01" value={formData.fee_percentage} onChange={(e) => setFormData({ ...formData, fee_percentage: e.target.value })} className="font-mono" data-testid="fee-pct-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Fee Fixo (€)</Label>
                    <Input type="number" step="0.01" value={formData.fee_fixed} onChange={(e) => setFormData({ ...formData, fee_fixed: e.target.value })} className="font-mono" data-testid="fee-fixed-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Aplica-se a (regimes)</Label>
                  <div className="flex flex-wrap gap-2">
                    {REGIMES.map((r) => (
                      <Badge key={r.value} variant={formData.applies_to.includes(r.value) ? "default" : "outline"} className="cursor-pointer" onClick={() => toggleRegime(r.value)}>
                        {r.label}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Descrição</Label>
                  <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Opcional" />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Ativo</Label>
                  <Switch checked={formData.active} onCheckedChange={(c) => setFormData({ ...formData, active: c })} />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" className="bg-brand-600 text-white hover:bg-brand-700">{editing ? "Guardar" : "Criar"}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Star className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Nenhum cliente especial configurado</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Cliente</TableHead>
                    <TableHead>Fee %</TableHead>
                    <TableHead>Fee Fixo</TableHead>
                    <TableHead>Regimes</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.id}>
                      <TableCell>
                        <p className="font-medium">{it.client_name}</p>
                        {it.description && <p className="text-xs text-muted-foreground">{it.description}</p>}
                      </TableCell>
                      <TableCell className="font-mono">{it.fee_percentage > 0 ? `${it.fee_percentage}%` : "—"}</TableCell>
                      <TableCell className="font-mono">{it.fee_fixed > 0 ? `€ ${it.fee_fixed}` : "—"}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(it.applies_to || []).map(r => <Badge key={r} variant="secondary" className="text-xs">{REGIMES.find(x => x.value === r)?.label || r}</Badge>)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(it)}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(it.id)}><Trash2 className="w-4 h-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminClientSpecialFees;
